import {
  AccessType,
  Dependency,
  featureProvider,
  type FeatureAccessResolver,
  type ResolvedAccess,
  type MembershipLevel,
} from '@onxmaps/feature-access'
import type { Vertical } from '@/types/verticals'

/**
 * Domain-wide Feature Access gates and resolvers.
 * These should only be used within vertical specific featureProviders.
 */

/** Domain-wide Feature Access Gate names */
export enum DomainFeatureGates {
  search_this_area = 'searchThisArea',
  search_map_markers = 'searchMapMarkers',
  private_land_search = 'privateLandSearch',
  open_in_console = 'openInConsole',
}

/** Resolver for the Open in Console feature */
export const resolveOpenInConsole: FeatureAccessResolver<
  Dependency.CLAIMS
> = async ({ getClaims }) => {
  const claims = await getClaims()
  if (claims.get('onx:employee')) {
    return { type: AccessType.HAS_ACCESS }
  }
  return { type: AccessType.NO_ACCESS }
}

/** Global Feature provider, for features that are based on Claims dependencies only */
export const globalFeatures = featureProvider({
  employeeSettings: {
    dependencies: [Dependency.CLAIMS],
    resolver: async ({ getClaims }) => {
      const claims = await getClaims()
      if ((claims.get('onx:employee') as { settings?: boolean })?.settings) {
        return { type: AccessType.HAS_ACCESS }
      }
    },
  },
  [DomainFeatureGates.open_in_console]: {
    dependencies: [Dependency.CLAIMS],
    resolver: resolveOpenInConsole,
  },
})

/**
 * Helper to generate a feature resolver for a specific vertical, based upon Membership dependency only.
 */
export const getVerticalMembershipFeatureAccessResolver: (
  vertical: Vertical,
  levelsWithoutAccess: MembershipLevel[]
) => FeatureAccessResolver<Dependency.MEMBERSHIP> = (
  vertical: Vertical,
  levelsWithoutAccess: MembershipLevel[]
) => {
  return async ({ getMembership }): Promise<ResolvedAccess> => {
    const membership = await getMembership()
    const membershipLevel = membership.get(vertical)?.membership_level

    if (membershipLevel && !levelsWithoutAccess.includes(membershipLevel)) {
      return { type: AccessType.HAS_ACCESS }
    }

    return { type: AccessType.NO_ACCESS }
  }
}

/** Resolver for the SearchThisArea feature */
export const getSearchThisAreaResolver: (
  vertical: Vertical
) => FeatureAccessResolver<Dependency.MEMBERSHIP> = (vertical: Vertical) => {
  return getVerticalMembershipFeatureAccessResolver(vertical, ['basic'])
}

/** Resolver for the SearchMapMarkers feature */
export const getSearchMapMarkersResolver: (
  vertical: Vertical
) => FeatureAccessResolver<Dependency.MEMBERSHIP> = (vertical: Vertical) => {
  return getVerticalMembershipFeatureAccessResolver(vertical, ['basic'])
}

/** Resolver for the Private Land Search feature, general access */
export const getPrivateLandSearchResolver: (
  vertical: Vertical
) => FeatureAccessResolver<Dependency.MEMBERSHIP> = (vertical: Vertical) => {
  return getVerticalMembershipFeatureAccessResolver(vertical, [
    'basic',
    'premium',
  ])
}
